function appendAndWait(container, node) {
    return new Promise((resolve) => {
        const observer = new MutationObserver((mutations) => {
            const hasAddedNodes = mutations.some((mutation) => mutation.addedNodes.length > 0);
            if (hasAddedNodes) {
                observer.disconnect();
                // requestAnimationFrame garantiert,
                // dass der Browser Zeit hatte,
                // das Layout neu zu berechnen (Reflow) und zu zeichnen (Repaint)
                requestAnimationFrame(() => resolve());
            }
        });
        observer.observe(container, { childList: true, subtree: true });
        container.appendChild(node);
    });
}

/**
 * Parst einen HTML-String zu einem Fragment. Enthaltene Scripte bleiben dabei ausführbar - anders als bei
 * innerHTML.
 *
 * @param {string} html - HTML-String
 * @returns {DocumentFragment}
 */
export function createFragment(html) {
    const range = document.createRange();
    range.setStart(document.documentElement, 0);
    return range.createContextualFragment(html);
}

/**
 * Rendert HTML in den Container und löst auf, wenn der DOM-Update sichtbar ist.
 *
 * @param {Element} container - Ziel-Container
 * @param {string} html - HTML-String
 * @returns {Promise<void>}
 */
export async function render(container, html) {
    return appendAndWait(container, createFragment(html));
}

/**
 * Hängt einen Node (HTMLElement, DocumentFragment, …) in den Container und löst auf, wenn der DOM-Update sichtbar ist.
 *
 * @param {Element} container - Ziel-Container
 * @param {DocumentFragment | Node} node - anzuhängender Node
 * @returns {Promise<void>}
 */
export async function renderNode(container, node) {
    return appendAndWait(container, node);
}

export function isFocusable(element) {
    if (!element || element.disabled || element.hidden) {
        return false;
    }
    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden') {
        return false;
    }
    const tabIndex = element.getAttribute('tabindex');
    if (tabIndex === '-1') {
        return false;
    }
    return true;
}

export function getFirstFocusableElement(container) {
    if (!container) return null;
    const focusableSelectors = [
        'button:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        'a[href]',
        '[tabindex]:not([tabindex="-1"])'
    ];
    const focusableElements = container.querySelectorAll(focusableSelectors.join(','));
    for (const element of focusableElements) {
        if (isFocusable(element)) {
            return element;
        }
    }
    return null;
}

/**
 * Sorgt dafür, dass ein Kind-Overlay, das über einem Parent-Overlay liegt,
 * mindestens dessen Höhe einnimmt. Ist das Kind-Overlay kleiner als das
 * Parent-Overlay, schaut das Parent-Overlay sonst darunter hervor.
 *
 * @param {object} params
 * @param {HTMLElement} params.childOverlay - das geöffnete Kind-Overlay
 * @param {HTMLElement} params.parentOverlay - das darunterliegende Parent-Overlay
 */
export function matchChildOverlayHeight({ childOverlay, parentOverlay }) {
    if (!childOverlay || !parentOverlay) {
        return;
    }
    if (childOverlay.offsetHeight < parentOverlay.offsetHeight) {
        childOverlay.style.setProperty("--element-overlay-slide-in-stack-height", `${parentOverlay.offsetHeight}px`);
    }
}

export function addLoadingState(submitter) {
    submitter.setAttribute("disabled", "");
    submitter.setAttribute("data-loading", "");
}

export function removeLoadingState(submitter) {
    submitter.removeAttribute("disabled");
    submitter.removeAttribute("data-loading");
}